"use client";

import { Core } from "@faire/design-tokens";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Row } from "@faire/web--source/slate/Layout";
import {
  Typography,
  TypographyProps,
  TypographyStyles,
} from "@faire/web--source/slate/Typography";
import {
  HIGH_VIS_FILTER,
  highVisSlateComponent,
} from "@faire/web--source/slate/utils/highVisSlateComponent";
import { useUniqueId } from "@faire/web--source/ui/hooks/useUniqueId";
import * as React from "react";
import styled, { css, useTheme } from "styled-components";

import { Spacer } from "../../slate/spacing";
import { themeOrDefault } from "../../slate/Theme";
import { ITooltipProps } from "../../slate/Tooltip/Tooltip";

import { getDerivedPropsFromErrorProps, IErrorProps } from "./error";

const inputBorderFocusStyles = css`
  &:focus,
  &:focus-within {
    border: 1px solid ${Core.border.default};
  }
`;

export const inputBorderStyles = css<{ $showErrorState?: boolean }>`
  border: 1px solid
    ${(props) =>
      props.$showErrorState ? Core.border.critical : Core.border.muted};

  &:invalid {
    border: 1px solid ${Core.border.critical};
  }

  ${inputBorderFocusStyles};
`;

export const INPUT_HEIGHT = 42;

export const disabledInputStylesNoBorder = css`
  background-color: ${Core.surface.subdued};
  cursor: default;
`;

export const inputStylesNoBorder = css<{ disabled?: boolean }>`
  ${(props) =>
    TypographyStyles({
      color: props.disabled ? Core.text.subdued : Core.text.default,
    })};
  appearance: none;
  border: none;
  outline: none;
  padding: 0 16px;

  &:invalid {
    appearance: none;
    box-shadow: none;
  }

  &::placeholder {
    color: ${({ theme }) =>
      themeOrDefault(theme).input.placeholder.color ?? Core.text.subdued};
  }

  &:disabled {
    ${disabledInputStylesNoBorder};
  }
  ${({ disabled }) => (disabled ? disabledInputStylesNoBorder : "")};
`;

export const inputStyles = css<{
  disabled?: boolean;
  $showErrorState?: boolean;
}>`
  ${inputStylesNoBorder};
  ${inputBorderStyles};
`;

export const InputWrapper = styled(Row).attrs({ align: "center" })<{
  $showErrorState?: boolean;
  $disabled?: boolean;
  $borderless?: boolean;
}>`
  ${({ $borderless: borderless }) =>
    !borderless ? inputBorderStyles : inputBorderFocusStyles};
  height: ${INPUT_HEIGHT}px;
  padding: 0 16px;
  background: ${Core.surface.inverse};
  cursor: text;
  ${({ $disabled: disabled }) =>
    disabled
      ? css`
          background-color: ${Core.surface.subdued};
        `
      : ""};
  ${highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT)}
`;

const InvisibleInput = styled.input`
  ${inputStylesNoBorder};
  padding: 0;
  @supports (display: flex) {
    width: 0;
    flex: 1 1 auto;
  }
  background: none;
  cursor: inherit;
  border: 0 !important;
`;

export const FormInputLabel = styled.label<{ $typography?: TypographyProps }>`
  ${({ $typography }) => TypographyStyles($typography ?? {})};
`;

const Wrapper = styled.div<{ $disableFlex: boolean | undefined }>`
  position: relative;
  flex: ${(props) => (props.$disableFlex ? "none" : 1)};
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type="number"] {
    -moz-appearance: textfield;
  }
  width: 100%;
`;

export interface IProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    IErrorProps {
  /** disables flex property on the input wrapper. */
  disableFlex?: boolean;
  /**
   * Label text, shown above the input field.
   */
  label?: React.ReactNode;
  /**
   * Adds a tooltip component next to the label. Label prop must also be set.
   */
  tooltip?: React.ReactElement<ITooltipProps>;
  /**
   * Helper text, shown below the input field.
   */
  helper?: string;
  /** React Node that will be placed before the text, but inside of the input border. */
  prefixNode?: React.ReactNode;
  /** React Node that will be placed after the text, but inside of the input border. */
  suffixNode?: React.ReactNode;
  className?: string;
  /** Hides the border of the input when it is not being focussed */
  borderless?: boolean;
  /** Hides the character count */
  hideCharacterCount?: boolean;
  sideCta?: React.ReactNode;
}

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/input/web/input)
 */
export const Input: React.FC<
  IProps & React.RefAttributes<HTMLInputElement>
> = ({
    disableFlex,
    error,
    label,
    tooltip,
    helper,
    prefixNode,
    suffixNode,
    className,
    onClick,
    borderless,
    id,
    "aria-label": ariaLabel,
    maxLength,
    hideCharacterCount,
    sideCta,
    ref,
    ...props
  }) => {
    const defaultId = useUniqueId("input-");
    const { input } = useTheme();
    const defaultRef = React.useRef<HTMLInputElement>(null);
    const innerRef = ref ?? defaultRef;

    const { showErrorState, showErrorMessage, errorMessage } =
      getDerivedPropsFromErrorProps({ error });

    const inputId = id ?? defaultId;
    const labelId = label ? `${inputId}__label` : undefined;
    const value: string = (props.value ?? "") as string;

    let ariaLabelProps = {};
    if (ariaLabel) {
      ariaLabelProps = { "aria-label": ariaLabel };
    } else if (labelId) {
      ariaLabelProps = { "aria-labelledby": labelId };
    }

    const shouldShowCharacterCount = !hideCharacterCount && !!maxLength;

    return (
      <Wrapper $disableFlex={disableFlex}>
        {label ? (
          <Row justify="space-between">
            <FormInputLabel
              id={labelId}
              $typography={{
                ...(input.label ?? {}),
                color: showErrorState ? Core.text.critical : input.label.color,
              }}
              htmlFor={inputId}
            >
              {tooltip ? (
                <Row>
                  {label}
                  <Spacer size="1x" />
                  {tooltip}
                </Row>
              ) : (
                label
              )}
            </FormInputLabel>
            {sideCta}
          </Row>
        ) : null}
        <InputWrapper
          className={className}
          $disabled={props.disabled}
          $showErrorState={showErrorState}
          onClick={(e) => {
            (
              innerRef as React.RefObject<HTMLInputElement | null>
            )?.current?.focus();
            if (!props.disabled) {
              onClick?.(e as React.MouseEvent<HTMLInputElement>);
            }
          }}
          role="button"
          $borderless={borderless}
        >
          {isNotUndefined(prefixNode) ? (
            <>
              {typeof prefixNode === "string" ? (
                <Typography>{prefixNode}</Typography>
              ) : (
                prefixNode
              )}
              <Spacer size={8} />
            </>
          ) : null}
          <InvisibleInput
            ref={innerRef}
            placeholder=""
            maxLength={maxLength}
            {...props}
            {...ariaLabelProps}
            id={inputId}
          />
          {isNotUndefined(suffixNode) ? (
            <>
              {typeof suffixNode === "string" ? (
                <Typography
                  color={
                    props.disabled ? Core.text.disabled : Core.text.default
                  }
                >
                  {suffixNode}
                </Typography>
              ) : (
                suffixNode
              )}
            </>
          ) : null}
        </InputWrapper>
        {shouldShowCharacterCount || errorMessage || helper ? (
          <FooterSection
            errorMessage={errorMessage}
            showErrorMessage={showErrorMessage}
            helper={helper}
            maxLength={maxLength}
            shouldShowCharacterCount={shouldShowCharacterCount}
            value={value}
          />
        ) : null}
      </Wrapper>
    );
  };

const HelperOrErrorText = ({
  helper,
  errorMessage,
  showErrorMessage,
}: IFooterSectionProps) => {
  if (showErrorMessage) {
    return (
      <Typography variant="labelSansRegular" color={Core.text.critical}>
        {errorMessage}
      </Typography>
    );
  } else {
    return (
      <Typography variant="labelSansRegular" color={Core.text.default}>
        {helper}
      </Typography>
    );
  }
};

interface IFooterSectionProps {
  helper?: string;
  showErrorMessage?: boolean;
  errorMessage?: string | React.ReactNode;
  maxLength?: number;
  shouldShowCharacterCount?: boolean;
  value?: string;
}

const FooterSection = ({
  helper,
  errorMessage,
  showErrorMessage,
  maxLength,
  shouldShowCharacterCount,
  value,
}: IFooterSectionProps) => {
  const textColor = showErrorMessage ? Core.text.critical : Core.text.default;

  return (
    <>
      <Spacer size={4} />
      <Row justify="space-between">
        <HelperOrErrorText
          helper={helper}
          errorMessage={errorMessage}
          showErrorMessage={showErrorMessage}
        />
        {shouldShowCharacterCount ? (
          <Typography variant="labelSansRegular" color={textColor}>
            {value?.length}/{maxLength}
          </Typography>
        ) : null}
      </Row>
    </>
  );
};
